import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table, { type TableProps } from '@cloudscape-design/components/table';
import { templateSizeLabel, type LabelTemplate } from '@etiquetachef/types';
import { AdminConfirm } from '@/components/ui/AdminConfirm';
import { formatDateTime } from '@/lib/format';
import { toast } from '@/lib/notifications';

type Props = {
  templates: LabelTemplate[];
  onSetDefault: (template: LabelTemplate) => Promise<void>;
  onDelete: (template: LabelTemplate) => Promise<void>;
};

type RowAction = 'setDefault' | 'delete';

/**
 * Tabela de modelos de etiqueta (admin, Cloudscape `Table` + `ButtonDropdown`).
 * Substitui o grid de `LabelTemplateCard` — expõe mais atributos em colunas
 * (tamanho, campos, margens, gap de linhas, ajuste vertical, status, atualizado
 * em). Componente puro: toda mudança de server-state passa pelos handlers.
 *
 * Origem: sem issue — commit a55e77f4c, 2026-06-10 14:05 +0200 (Modelos em tabela).
 *
 * Contrato: erro do "definir padrão" vira toast (não há modal aberto); erro da
 * exclusão fica num `Alert` dentro do confirm, que continua aberto (#1021).
 *
 * Usado por: `features/labelTemplates/LabelTemplatesTab.tsx`.
 *
 * Histórico:
 *  - 2026-10-25 15:00 +0100 — (#1021) erro da exclusão em `Alert` no confirm: o
 *    toast ficava atrás do overlay (mesmo achado do CRM, #174).
 *  - 2026-10-25 21:32 +0100 — (#1021, 2ª auditoria) docstring movida para cima do
 *    `export function` (estava sobre `type Props`, e o gate de cobertura não a via).
 */
export function LabelTemplatesTable({
  templates,
  onSetDefault,
  onDelete,
}: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [pendingDelete, setPendingDelete] = useState<LabelTemplate | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // #1021 (2026-10-25 15:00 +0100): erro da exclusão, mostrado dentro do confirm.
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function handleSetDefault(template: LabelTemplate): Promise<void> {
    setBusyId(template.id);
    try {
      await onSetDefault(template);
      toast.success(t('labelTemplates.toast.defaultSet'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'));
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(): Promise<void> {
    if (!pendingDelete) return;
    setBusyId(pendingDelete.id);
    setDeleteError(null);
    try {
      await onDelete(pendingDelete);
      toast.success(t('labelTemplates.toast.deleted'));
      setPendingDelete(null);
    } catch (err) {
      // Auditoria 2026-10-25 15:00 +0100 (#1021): era `toast.error`, atrás do overlay do confirm, que
      // não fecha no erro (mesmo achado do CRM, #174). Vai para o `Alert` dentro dele.
      setDeleteError(err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'));
    } finally {
      setBusyId(null);
    }
  }

  const columns: TableProps.ColumnDefinition<LabelTemplate>[] = [
    {
      id: 'name',
      header: t('labelTemplates.columns.name'),
      cell: (tpl): React.ReactNode => <Box fontWeight="bold">{tpl.name}</Box>,
      sortingField: 'name',
      isRowHeader: true,
    },
    {
      id: 'size',
      header: t('labelTemplates.columns.size'),
      cell: (tpl): React.ReactNode => templateSizeLabel(tpl.widthMm, tpl.heightMm),
    },
    {
      id: 'fields',
      header: t('labelTemplates.columns.fields'),
      cell: (tpl): React.ReactNode => tpl.includedFields.length,
    },
    {
      id: 'gap',
      header: t('labelTemplates.columns.gap'),
      cell: (tpl): React.ReactNode => tpl.gapMm,
    },
    {
      id: 'rowGap',
      header: t('labelTemplates.columns.rowGap'),
      cell: (tpl): React.ReactNode => tpl.rowGapDots,
    },
    {
      id: 'verticalFit',
      header: t('labelTemplates.columns.verticalFit'),
      cell: (tpl): React.ReactNode => t(`labelTemplates.verticalFit.${tpl.verticalFit}`),
    },
    {
      id: 'status',
      header: t('labelTemplates.columns.status'),
      cell: (tpl): React.ReactNode => (
        <SpaceBetween direction="horizontal" size="xs">
          {tpl.isDefault && <Badge color="green">{t('labelTemplates.badge.default')}</Badge>}
          {tpl.active ? (
            !tpl.isDefault && <Badge color="grey">{t('labelTemplates.columns.statusActive')}</Badge>
          ) : (
            <Badge color="red">{t('labelTemplates.badge.inactive')}</Badge>
          )}
        </SpaceBetween>
      ),
    },
    {
      id: 'updatedAt',
      header: t('labelTemplates.columns.updatedAt'),
      cell: (tpl): React.ReactNode => formatDateTime(tpl.updatedAt, locale),
      sortingField: 'updatedAt',
    },
    {
      id: 'actions',
      header: t('labelTemplates.columns.actions'),
      cell: (tpl): React.ReactNode => (
        <ButtonDropdown
          expandToViewport
          loading={busyId === tpl.id}
          disabled={busyId !== null}
          items={[
            {
              id: 'setDefault',
              text: t('labelTemplates.actions.setDefault'),
              disabled: tpl.isDefault,
            },
            {
              id: 'delete',
              text: t('labelTemplates.actions.delete'),
              disabled: tpl.isDefault,
            },
          ]}
          onItemClick={({ detail }): void => {
            const action = detail.id as RowAction;
            if (action === 'setDefault') {
              void handleSetDefault(tpl);
            } else {
              setDeleteError(null);
              setPendingDelete(tpl);
            }
          }}
        >
          {t('labelTemplates.columns.actions')}
        </ButtonDropdown>
      ),
    },
  ];

  return (
    <>
      <Table<LabelTemplate>
        items={templates}
        trackBy="id"
        variant="container"
        columnDefinitions={columns}
        empty={
          <Box textAlign="center" color="inherit">
            {t('labelTemplates.empty.message')}
          </Box>
        }
      />

      <AdminConfirm
        visible={pendingDelete !== null}
        title={t('labelTemplates.deleteDialog.title')}
        confirmLabel={t('labelTemplates.actions.confirmDelete')}
        cancelLabel={t('tenantDetail.actions.cancel')}
        loading={busyId !== null && pendingDelete !== null}
        onConfirm={(): void => {
          void handleDelete();
        }}
        onDismiss={(): void => {
          if (busyId === null) setPendingDelete(null);
        }}
      >
        <SpaceBetween size="m">
          <Box variant="p">
            {t('labelTemplates.deleteDialog.description', { name: pendingDelete?.name ?? '' })}
          </Box>
          {/* Auditoria 2026-10-25 15:00 +0100 (#1021): erro dentro do confirm, junto do footer (#174). */}
          {deleteError !== null && <Alert type="error">{deleteError}</Alert>}
        </SpaceBetween>
      </AdminConfirm>
    </>
  );
}
